<div class="home">
  <!-- ── Hero ────────────────────────────────────────────────────────────── -->
  <section class="hero">
    <span class="hero__grid"></span>
    <span class="hero__glow"></span>
    <span class="hero__glow hero__glow--2"></span>

    <div class="hero__inner">
      <span class="hero__badge">
        <span class="hero__badge-dot"></span>
        {{ "portal.HeroBadge" | translate }}
      </span>

      <h1 class="hero__title">
        {{ heroTitle }}
        <span class="hero__title-grad" *ngIf="heroAccent">{{ heroAccent }}</span>
      </h1>

      <p class="hero__lead">
        {{ c('hero.subtitle', t('portal.HeroSubFallback')) }}
      </p>

      <div class="hero__cta">
        <a routerLink="/portal/become-a-tenant" class="btn-blue">
          {{ c('hero.cta', t('portal.RequestTenant')) }} <i class="bi bi-arrow-right portal-arrow"></i>
        </a>
        <a routerLink="/portal/find-teachers" class="btn-ghost">
          <i class="bi bi-search"></i> {{ "portal.FindTeacher" | translate }}
        </a>
      </div>

      <div class="hero__stats" *ngIf="stats.length">
        <div class="hero__stat" *ngFor="let s of stats">
          <div class="hero__stat-value">{{ statValue(s) }}</div>
          <div class="hero__stat-label">{{ statLabel(s) }}</div>
        </div>
      </div>
    </div>

    <svg class="hero__wave" viewBox="0 0 1440 80" preserveAspectRatio="none" fill="none">
      <path d="M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z" fill="#f0f2fa" />
    </svg>
  </section>

  <!-- ── Showcase slider (optional) ──────────────────────────────────────── -->
  <section class="slider reveal" *ngIf="showSlider && slides.length"
    (mouseenter)="pauseSlider()" (mouseleave)="resumeSlider()">
    <div class="slider__frame">
      <ng-container *ngFor="let s of slides; let i = index">
        <div class="slider__slide" [class.is-active]="i === activeSlide">
          <img [src]="slideImg(s)" [alt]="slideCaption(s)" />
          <span class="slider__caption" *ngIf="slideCaption(s)">{{ slideCaption(s) }}</span>
        </div>
      </ng-container>
      <ng-container *ngIf="slides.length > 1">
        <button type="button" class="slider__arrow slider__arrow--prev" (click)="prevSlide()" [attr.aria-label]="'portal.Previous' | translate">‹</button>
        <button type="button" class="slider__arrow slider__arrow--next" (click)="nextSlide()" [attr.aria-label]="'portal.Next' | translate">›</button>
      </ng-container>
    </div>
    <div class="slider__dots" *ngIf="slides.length > 1">
      <button type="button" class="slider__dot" *ngFor="let s of slides; let i = index"
        [class.is-active]="i === activeSlide" (click)="goToSlide(i)" [attr.aria-label]="t('portal.SlideN', { n: i + 1 })"></button>
    </div>
  </section>

  <!-- ── Features ────────────────────────────────────────────────────────── -->
  <section class="section reveal">
    <div class="section__head">
      <p class="kicker">{{ c('features.kicker', t('portal.FeaturesKicker')) }}</p>
      <h2 class="section__title">{{ c('features.title', t('portal.FeaturesTitle')) }}</h2>
    </div>
    <div class="features">
      <div class="feature" *ngFor="let f of displayFeatures">
        <div class="feature__icon"><i class="bi {{ f.icon }}"></i></div>
        <h3 class="feature__title">{{ f.title }}</h3>
        <p class="feature__text">{{ f.text }}</p>
      </div>
    </div>
  </section>

  <!-- ── How it works ────────────────────────────────────────────────────── -->
  <section class="steps-band reveal">
    <div class="section__head">
      <p class="kicker kicker--light">{{ c('steps.kicker', t('portal.StepsKicker')) }}</p>
      <h2 class="section__title section__title--light">{{ c('steps.title', t('portal.StepsTitle')) }}</h2>
    </div>
    <div class="steps">
      <div class="step" *ngFor="let s of displaySteps; let i = index">
        <div class="step__num">{{ (i + 1) < 10 ? '0' + (i + 1) : (i + 1) }}</div>
        <h3 class="step__title">{{ s.title }}</h3>
        <p class="step__text">{{ s.text }}</p>
      </div>
    </div>
  </section>

  <!-- ── Testimonials ────────────────────────────────────────────────────── -->
  <section class="section reveal" *ngIf="testimonials.length">
    <div class="section__head">
      <p class="kicker">{{ c('testimonials.kicker', t('portal.TestimonialsKicker')) }}</p>
      <h2 class="section__title">{{ c('testimonials.title', t('portal.TestimonialsTitle')) }}</h2>
    </div>
    <div class="tms">
      <figure class="tm" *ngFor="let t of testimonials">
        <div class="tm__stars"><i class="bi bi-star-fill" *ngFor="let _ of [1,2,3,4,5]"></i></div>
        <blockquote class="tm__quote">“{{ tmQuote(t) }}”</blockquote>
        <figcaption class="tm__author">
          <span class="tm__avatar">{{ tmInitial(t) }}</span>
          <span class="tm__meta">
            <span class="tm__name">{{ tmAuthor(t) }}</span>
            <span class="tm__role" *ngIf="tmRole(t)">{{ tmRole(t) }}</span>
          </span>
        </figcaption>
      </figure>
    </div>
  </section>

  <!-- ── Pricing ─────────────────────────────────────────────────────────── -->
  <section class="section section--tint reveal" *ngIf="plans.length" id="pricing">
    <div class="section__head">
      <p class="kicker">{{ c('pricing.kicker', t('portal.Pricing')) }}</p>
      <h2 class="section__title">{{ c('pricing.title', t('portal.PricingTitle')) }}</h2>
      <p class="section__sub">{{ c('pricing.subtitle', t('portal.PricingSubtitle')) }}</p>
    </div>

    <div class="pricing__controls">
      <div class="seg" role="group" [attr.aria-label]="'portal.BillingInterval' | translate">
        <button type="button" [class.is-active]="interval === 1" (click)="setInterval(1)">{{ "portal.Monthly" | translate }}</button>
        <button type="button" [class.is-active]="interval === 2" (click)="setInterval(2)">
          {{ "portal.Annual" | translate }} <span class="seg__badge">{{ "portal.TwoMonthsFree" | translate }}</span>
        </button>
      </div>
      <div class="seg" role="group" [attr.aria-label]="'portal.Currency' | translate">
        <button type="button" *ngFor="let cur of currencies"
          [class.is-active]="selectedCurrency === cur" (click)="setCurrency(cur)">{{ cur }}</button>
      </div>
    </div>

    <div class="plans">
      <div class="plan" *ngFor="let p of plans; let i = index" [class.is-popular]="isPopular(i)">
        <span class="plan__badge" *ngIf="isPopular(i)">{{ "portal.MostPopularShort" | translate }}</span>
        <h3 class="plan__name">{{ planName(p) }}</h3>
        <p class="plan__desc" *ngIf="planDescription(p)">{{ planDescription(p) }}</p>

        <div class="plan__price" *ngIf="priceFor(p) != null; else contactUs">
          <span class="plan__amount">{{ priceFor(p) | number:'1.0-0' }}</span>
          <span class="plan__cur">{{ selectedCurrency }}</span>
          <span class="plan__per">/ {{ (interval === 2 ? "portal.PerYear" : "portal.PerMonth") | translate }}</span>
        </div>
        <ng-template #contactUs>
          <div class="plan__price plan__price--contact">{{ "portal.ContactUs" | translate }}</div>
        </ng-template>

        <div class="plan__trial" *ngIf="p.trialDays > 0">{{ t('portal.TrialDays', { n: p.trialDays }) }}</div>

        <ul class="plan__features">
          <li *ngFor="let feat of planFeatures(p)" [class.is-off]="!feat.ok">
            <i class="bi" [class.bi-check-lg]="feat.ok" [class.bi-dash]="!feat.ok"></i>{{ feat.label }}
          </li>
        </ul>

        <a routerLink="/portal/become-a-tenant" class="plan__cta" [class.is-popular]="isPopular(i)">{{ "portal.GetStarted" | translate }}</a>
      </div>
    </div>
  </section>

  <!-- ── FAQ ─────────────────────────────────────────────────────────────── -->
  <section class="section reveal" *ngIf="faqs.length" id="faq">
    <div class="section__head">
      <p class="kicker">{{ c('faq.kicker', t('portal.FaqKicker')) }}</p>
      <h2 class="section__title">{{ c('faq.title', t('portal.FaqTitle')) }}</h2>
    </div>
    <ul class="faq">
      <li class="faq__item" *ngFor="let f of faqs; let i = index" [class.is-open]="openFaq === i">
        <button type="button" class="faq__q" (click)="toggleFaq(i)" [attr.aria-expanded]="openFaq === i">
          <span>{{ faqQuestion(f) }}</span>
          <i class="bi bi-chevron-down faq__chevron"></i>
        </button>
        <div class="faq__a" *ngIf="openFaq === i">{{ faqAnswer(f) }}</div>
      </li>
    </ul>
  </section>

  <!-- ── CTA band ────────────────────────────────────────────────────────── -->
  <section class="section reveal">
    <div class="cta">
      <h2 class="cta__title">{{ c('cta.title', t('portal.CtaTitle')) }}</h2>
      <p class="cta__sub">{{ c('cta.subtitle', t('portal.CtaSubtitle')) }}</p>
      <div class="cta__btns">
        <a routerLink="/portal/become-a-tenant" class="btn-blue">
          {{ c('cta.button', t('portal.RequestTenant')) }} <i class="bi bi-arrow-right portal-arrow"></i>
        </a>
        <a routerLink="/portal/find-teachers" class="btn-ghost">{{ "portal.FindTeacher" | translate }}</a>
      </div>
    </div>
  </section>
</div>
